import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";
import {
  DEFAULT_FLOATING_CHAT_SIZE,
  resizeFloatingChat,
  type FloatingChatResizeEdge,
  type FloatingChatSize,
} from "./floatingChatSize";

const EDGE_CURSORS: Record<FloatingChatResizeEdge, string> = {
  left: "ew-resize",
  top: "ns-resize",
  "top-left": "nwse-resize",
};

/** 右下の小窓を左端・上端・左上角のドラッグでリサイズする。 */
export const useResizableFloatingChat = () => {
  const [size, setSize] = useState<FloatingChatSize>(
    DEFAULT_FLOATING_CHAT_SIZE,
  );
  const cleanupRef = useRef<() => void>();

  const startResize = useCallback(
    (edge: FloatingChatResizeEdge) =>
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        event.preventDefault();
        cleanupRef.current?.();

        const startX = event.clientX;
        const startY = event.clientY;
        const start = size;
        document.body.style.cursor = EDGE_CURSORS[edge];
        document.body.style.userSelect = "none";

        const handleMouseMove = (moveEvent: MouseEvent) => {
          setSize(
            resizeFloatingChat({
              start,
              edge,
              deltaX: moveEvent.clientX - startX,
              deltaY: moveEvent.clientY - startY,
              viewport: {
                width: window.innerWidth,
                height: window.innerHeight,
              },
            }),
          );
        };
        const finish = () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", finish);
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
          cleanupRef.current = undefined;
        };

        cleanupRef.current = finish;
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", finish);
      },
    [size],
  );

  useEffect(() => () => cleanupRef.current?.(), []);

  return { size, startResize };
};
